import PropTypes from 'prop-types';
import { Box, Stack, Typography } from '@mui/material';

import { TitleTooltip } from '../TitleTooltip';
import ButtonNavigation from '../ButtonNavigation';
import { Select } from '../Select';

const styles = {
  container: ({ palette }) => ({
    display: 'flex',
    width: '100%',
    height: '100%',
    border: `2px solid ${palette.colors.darkPale}`,
    borderRadius: '16px',
    overflow: 'hidden',
    flexDirection: 'column',
  }),
  header: ({ breakpoints, palette }) => ({
    height: 50,
    flex: 'none',
    padding: '10px',
    display: 'flex',
    flexDirection: 'column',
    gap: 1.5,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: palette.colors.lightPale,
    borderBottom: `2px solid ${palette.colors.darkPale}`,
    position: 'relative',
    borderRadius: '16px 16px 0 0',

    [breakpoints.up('sm')]: {
      padding: '20px',
    },

    [breakpoints.up('md')]: {
      height: 75,
    },
  }),
  headerContent: ({ breakpoints }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    textAlign: 'center',
    alignItems: 'center',
    width: '100%',
    gap: 1,

    [breakpoints.up('md')]: {
      justifyContent: 'center',
    },
  }),
  content: ({ breakpoints }) => ({
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    height: '100%',
    minHeight: 280,
    padding: '24px',
    [breakpoints.up('md')]: {
      minHeight: 400,
    },
  }),
  selectWrapper: ({ breakpoints }) => ({
    position: 'relative',
    padding: '0 15px',
    width: 300,
    height: 48,

    [breakpoints.up('md')]: {
      flex: '1 0',
      display: 'flex',
      position: 'absolute',
      justifyContent: 'flex-end',
      right: 0,
    },
  }),
  select: {
    width: '100%',
  },
};

const DashboardBox = ({
  customStyles = {},
  customHeaderContentStyles = {},
  customHeaderStyles = {},
  customContentStyles = {},
  title = '',
  children = null,
  subTitle = '',
  withSubtitleTooltip = false,
  next = null,
  prev = null,
  custom = null,
  selectValue = null,
  onChangeSelect = null,
  selectOptions = [],
  subTitleLength = 0,
}) => {
  return (
    <Box sx={[styles.container, customStyles]}>
      <Box sx={[styles.header, customHeaderStyles]}>
        {next ? (
          <Box sx={[styles.headerContent, customHeaderContentStyles]}>
            <ButtonNavigation isPrev onClick={prev} />

            <Stack gap={1} alignItems="center">
              <Typography variant="body1" textAlign="center">
                {title}
              </Typography>

              {subTitle &&
                (withSubtitleTooltip ? (
                  <TitleTooltip
                    title={subTitle}
                    titleLength={subTitleLength}
                    variant="body1"
                    color="primary"
                    textAlign="center"
                  />
                ) : (
                  <Typography variant="body1" color="primary" textAlign="center">
                    {subTitle}
                  </Typography>
                ))}
            </Stack>
            <ButtonNavigation onClick={next} />
          </Box>
        ) : (
          custom?.() || (
            <Typography variant="body1" textAlign="center">
              {title}
            </Typography>
          )
        )}

        {onChangeSelect && (!!selectValue || selectValue === 0) && (
          <Box sx={styles.selectWrapper}>
            <Select
              value={selectValue}
              options={selectOptions}
              onChange={onChangeSelect}
              customStyles={{ select: styles.select }}
            />
          </Box>
        )}
      </Box>

      <Box sx={[styles.content, customContentStyles]}>{children}</Box>
    </Box>
  );
};

const stylesPropTypes = PropTypes.oneOfType([PropTypes.func, PropTypes.shape({})]);

DashboardBox.propTypes = {
  customStyles: stylesPropTypes,
  customHeaderStyles: stylesPropTypes,
  customHeaderContentStyles: stylesPropTypes,
  customContentStyles: stylesPropTypes,
  title: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.element, PropTypes.string]),
  subTitle: PropTypes.string,
  withSubtitleTooltip: PropTypes.bool,
  next: PropTypes.func,
  prev: PropTypes.func,
  custom: PropTypes.func,
  selectValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChangeSelect: PropTypes.func,
  selectOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ),
  subTitleLength: PropTypes.number,
};
export default DashboardBox;
